import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Input} from "@/components/input/Input.tsx";
import {getFormError} from "@/lib/form/form.utils.ts";
import {getRequiredError} from "@/pages/checks/components/form/constants.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";
import {cloneElement, ReactElement} from "react";
import {RegisterOptions, useFormContext} from "react-hook-form";

export type InputFieldProps = FieldProps & {
  type?: string;
  maxLength?: number;
  disabled?: boolean;
  options?: RegisterOptions;
  input?: ReactElement;
  hideErrorMessage?: boolean;
};

export const InputField = ({
  id,
  name,
  label,
  className,
  tooltip,
  supportLink,
  helpText,
  required,
  placeholder,
  maxLength,
  disabled = false,
  autoFocus = false,
  hideErrorMessage = false,
  options = {},
  labelIcon,
  input,
  labelTypography,
  hasRequiredLabel,
  formSubtextClassName,
  dataTestId: dataTestid,
  invalid,
}: InputFieldProps) => {
  const {
    register,
    formState: {errors},
  } = useFormContext();
  const fieldError = getFormError(name, errors);
  if (required) {
    options.required = getRequiredError(name);
  }

  const commonProps = {
    id,
    maxLength,
    disabled,
    placeholder,
    "aria-invalid": !!fieldError || !!invalid,
    autoFocus,
    // React applies autoFocus imperatively without rendering the attribute; this
    // marker lets containers (e.g. modals) re-focus the field after transitions
    "data-autofocus": autoFocus || undefined,
    dataTestid,
  };

  return (
    <div className={className}>
      {label && (
        <FormLabel
          helpText={tooltip}
          forId={id || name}
          required={required}
          icon={labelIcon}
          hasRequiredLabel={hasRequiredLabel}
          labelTypography={labelTypography}
        >
          {label}
        </FormLabel>
      )}
      <>
        {input ? (
          cloneElement(input, {
            ...commonProps,
            ...input.props,
          })
        ) : (
          <Input {...commonProps} {...register(name, options)} type="text" autoComplete="off" />
        )}
      </>
      <FormSubtext
        subText={helpText}
        supportLink={supportLink}
        error={hideErrorMessage ? "" : fieldError?.toString()}
        className={formSubtextClassName}
      />
    </div>
  );
};
